/**
 * Generates a universally unique identifier (UUID v4).
 * 
 * Uses the native `crypto.randomUUID()` API when available (modern browsers),
 * which provides cryptographically secure random values. Falls back to a
 * Math.random()-based implementation for older browsers (e.g., Safari < 15.4).
 * 
 * @returns A UUID v4 string in the format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * @example
 * const id = generateUUID()
 * // Returns something like: "550e8400-e29b-41d4-a716-446655440000"
 */
export function generateUUID(): string {
  // Use native crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  // Fallback for older browsers (especially mobile Safari < 15.4)
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // The '4' indicates version 4, and 'y' is constrained to 8, 9, A, or B
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
